// Define pricing types directly to avoid circular dependencies
export enum PricingTier {
  Free = 'Free',
  Basic = 'Basic',
  Pro = 'Pro',
}

export enum BillingCycle {
  Monthly = 'Monthly',
  Annually = 'Annually',
}

export interface FileAttachment {
  id: string;
  name: string; // Display name (original filename)
  fileId?: string; // Backend file ID/path (e.g., "uploads/1755152083049-68375608df613d98...")
  size: number;
  type: string; // 'application/pdf'
  uploadDate: Date;
  uploadStatus: 'uploading' | 'completed' | 'failed';
  url?: string; // URL after successful upload
  error?: string; // Error message if upload failed
}

export interface StickyNote {
  id: string;
  content: string;
  position: {
    x: number;
    y: number;
  };
  color: string;
  zIndex: number;
  width: number;
  height: number;
  parentId?: string; // null or undefined for root notes, otherwise points to parent note
  level?: number; // 0 for root, 1 for first branch, 2 for second branch, 3 for third branch
  showConverge?: boolean;
  showConvergeGlow?: boolean; // Temporary glow effect for new converge lines
  mergeTargetId?: string; // ID of the note this branch was merged into
  topic?: string; // AI-inferred topic for the note heading
  hasUserSetTopic?: boolean; // Flag to track if user manually set a meaningful topic
  chatHistory?: Array<{ sender: 'user' | 'ai'; text: string }>; // Per-note chat history
  mergeMessages?: Array<{ // Metadata for merge summary messages
    messageIndex: number;
    sourceBranchId: string;
    sourceBranchName: string;
  }>;

  copyMessages?: Array<{ // Metadata for copied messages
    messageIndex: number;
    sourceNoteId: string;
    sourceNoteName: string;
  }>;
  status?: 'root' | 'active' | 'merged' | 'archived'; // Status of the note
  isCreatingBranch?: boolean; // Visual feedback: parent is creating a branch
  isNewBranch?: boolean; // Visual feedback: this note is a newly created branch
  systemPrompt?: string; // Original detailed system prompt for this note
  persona?: string; // Extracted persona/role title (e.g., "Software Architect")
  hasCustomSystemPrompt?: boolean; // Flag to track if custom prompt is set
  firstMessageSent?: boolean; // Track if first message has been sent
  needsPersonaSelection?: boolean; // Flag to indicate persona selection is needed for new notes
  inheritedSystemPrompt?: string; // Original system prompt inherited from parent note (for branch notes)
  attachedFiles?: FileAttachment[]; // Files attached to this note
}

// Branch inheritance types
export type InheritanceType = 'full' | 'summary' | 'empty';

// Branch relationship tracking
export interface BranchRelationship {
  parentId: string;
  branchPoint: number; // index in parent's chat history where branch was created
  branchId: string;
  inheritanceType: InheritanceType;
  createdAt: Date;
}

// File reference interface for messages
export interface MessageFile {
  fileName: string;
  originalName: string;
  fileType: string;
  downloadUrl: string;
  size: number;
  expiresAt: string;
}

// Enhanced message interface for langgraph-advanced responses
export interface EnhancedMessage {
  sender: 'user' | 'ai';
  text: string;
  suggestions?: string[];
  analysis?: {
    intent: string;
    tone: string;
    confidence: number;
  };
  topic?: string;
  files?: MessageFile[]; // Files attached to this message
  thought_thread?: string[]; // AI reasoning thoughts for collapsible display
}

export interface WhiteboardState {
  notes: StickyNote[];
  selectedNoteIds: string[];
  scale: number;
  offset: { x: number; y: number };
  isSelectionMode: boolean;
  selectionBox: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null;
  branchRelationships: BranchRelationship[]; // Track all branch relationships
  canvasName?: string; // Current canvas name
  canvasId?: string;
  description?: string;
  tags?: string[];
  lastSavedStateHash?: string;
  hasEverBeenSaved?: boolean;
}

export type WhiteboardAction =
  | { type: 'ADD_NOTE'; note: StickyNote }
  | { type: 'UPDATE_NOTE'; id: string; updates: Partial<StickyNote> }
  | { type: 'DELETE_NOTE'; id: string }
  | { type: 'DELETE_SELECTED_NOTES' }
  | { type: 'SELECT_NOTE'; id: string; isMultiSelect: boolean }
  | { type: 'DESELECT_ALL' }
  | { type: 'SET_SELECTION_MODE'; isSelectionMode: boolean }
  | { type: 'START_SELECTION_BOX'; startX: number; startY: number }
  | { type: 'UPDATE_SELECTION_BOX'; endX: number; endY: number }
  | { type: 'END_SELECTION_BOX' }
  | { type: 'ZOOM'; scale: number }
  | { type: 'PAN'; offsetX: number; offsetY: number };

// User Profile and Subscription Types
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  createdAt: Date;
  stripeCustomerId?: string;
}

export interface UserSubscription {
  id: string; // Stripe subscription ID
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired';
  priceId: string;
  productId: string;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  createdAt: Date | null;
  canceledAt?: Date | null;
  endedAt?: Date | null;
}

export interface UserQuota {
  // User-level quotas only
  totalCanvases: number;
  maxTotalCanvases: number;
  threadStarters: number;
  maxThreadStarters: number;
  fileAttachments: number;
  maxFileAttachments: number;
  webSearches: number;
  maxWebSearches: number;
  totalThreads: number;
  maxTotalThreads: number;
  monthlyChatMessages: number;
  maxMonthlyChatMessages: number;
  resetPeriod: 'monthly' | 'never';
  lastReset: Date;
}

// Remove CanvasQuota interface - no more per-canvas tracking

// Stripe-related types
export interface StripeCheckoutSession {
  sessionId: string;
  url: string;
}

export interface StripePriceConfig {
  priceId: string;
  productId: string;
  tier: PricingTier;
  billingCycle: BillingCycle;
}

export interface SubscriptionLimits {
  conversationalMessages: number | 'unlimited';
  threadStarters: number | 'unlimited';
  threadIdeas: number | 'unlimited';
  totalNotes: number | 'unlimited';
  fileAttachments: number | 'unlimited';
  mergeOperations: number | 'unlimited';
  webSearches: number | 'unlimited';
}

